var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { usersService } from "../multiplayer/UsersService";
import { preferencesService } from "../preferences/PreferencesService";
import { remoteStreamingService } from "../multiplayer/peer";
import { createAudioIndicator, stopAudioIndicator, soundService } from "../audio";
export class AvatarAudio {
    constructor(uid /*, private avatarGroup: Group */) {
        this.uid = uid;
        this.isMuted = false;
        this.isAudible = true;
        this.updateMicStreamFn = this.updateMicStream.bind(this);
        // TODO: avatarGroup is relevant for Positional Audio
        remoteStreamingService.onUpdateDelegate.add(this.updateMicStreamFn);
        preferencesService.onPreferenceChanged.add((key) => __awaiter(this, void 0, void 0, function* () {
            if (key === "speakersDeviceId") {
                this.updateMicStream();
                return true;
            }
            return false;
        }));
        // TODO: When Positional Audio is used, we need to update when the user changes the settings:
        /* preferencesService.onPreferenceChanged.add(async (key: string) => {
          if (key !== "disablePositionalAudio") //Expand to all other relevant preferences
            return false
          this.updateMicStream()
          return true
        }) */
        this.updateMicStream();
    }
    destroy() {
        remoteStreamingService.onUpdateDelegate.remove(this.updateMicStreamFn);
        this.clearAudio();
    }
    updateMicStream() {
        const clientId = usersService.getClientIdByAvatarUid(this.uid);
        if (clientId === undefined) {
            this.mediaStream = undefined;
            this.clearAudio();
            return;
        }
        const updateStream = () => {
            var _a;
            const remoteStream = remoteStreamingService.getMicrophoneStreams().find((s) => s.clientId === clientId);
            let maxTries = 50;
            if (remoteStream) {
                if (remoteStream.stream.getAudioTracks().length > 0) {
                    const currentStreamId = (_a = this.mediaStream) === null || _a === void 0 ? void 0 : _a.id;
                    this.mediaStream = remoteStream.stream;
                    if (currentStreamId === remoteStream.stream.id) {
                        this.updatePlaybackVolume(clientId);
                    }
                    else {
                        void this.initAudio();
                    }
                }
                else {
                    if (maxTries-- > 0) {
                        console.warn("AvatarAudio.applyMediaStream: stream has no audio tracks, trying again in 100ms", remoteStream.stream);
                        setTimeout(updateStream, 100);
                    }
                    else {
                        console.error("AvatarAudio.applyMediaStream: stream has no audio tracks, giving up", remoteStream.stream);
                    }
                }
            }
            else {
                this.mediaStream = undefined;
                this.clearAudio();
            }
        };
        updateStream();
    }
    clearAudio() {
        // TODO: Relevant for Positional Audio
        /* if (this.microphoneSource) {
          this.microphoneSource.disconnect()
          this.microphoneSource = undefined
        }
    
        if (this.destination) {
          this.destination.disconnect()
          this.destination = undefined
        } */
        if (this.audioElement) {
            this.audioElement.remove();
            this.audioElement = undefined;
        }
        if (this.audioIndicator) {
            stopAudioIndicator(this.audioIndicator);
            this.audioIndicator = undefined;
        }
        // TODO: Relevant for Positional Audio
        /* if (this.positionalAudio) {
          if (this.positionalAudio.isPlaying) {
            this.positionalAudio.stop()
          }
          this.positionalAudio.disconnect()
          this.avatarGroup.remove(this.positionalAudio)
          this.positionalAudio = undefined
        } */
        this.setIsMuted(true);
    }
    initAudio() {
        return __awaiter(this, void 0, void 0, function* () {
            this.clearAudio();
            if (this.mediaStream) {
                const clientId = usersService.getClientIdByAvatarUid(this.uid);
                this.audioElement = new Audio();
                this.audioElement.id = "micstream-" + this.mediaStream.id;
                this.audioElement.setAttribute("playsinline", "playsinline");
                this.audioElement.volume = 1; // TODO: Set to 0 when using Positional Audio
                document.body.appendChild(this.audioElement);
                this.audioElement.srcObject = this.mediaStream;
                // Workaround for Chrome autoplay policy, see https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
                //
                // This ensures, that at least the audio gets activated as soon as the first user interaction happens (Chrome not blocking anymore)
                // while it still plays immediately if chrome is not blocking (User already has interacted with the page before).
                //
                // Other solutions like initially muting the audio and unmuting it on user interaction did not work
                // as they mute EVERY audio until the user interacts, even if the user has interacted with the page before.
                //
                // Unfortunately there's no way to find out if chrome is currently blocking other than just trying to call play() and see if it works.
                const play = () => __awaiter(this, void 0, void 0, function* () {
                    try {
                        if (this.audioElement) {
                            yield this.audioElement.play();
                        }
                    }
                    catch (_) {
                        setTimeout(play, 50);
                    }
                });
                play();
                try {
                    yield soundService.applySelectedSpeakersToAudioElement(this.audioElement);
                }
                catch (error) {
                    console.error("setSelectedSpeakersToAudioElement", error);
                }
                const indicator = createAudioIndicator(this.mediaStream, () => {
                    if (this.volumeIndicator)
                        this.volumeIndicator.visible = true;
                }, () => {
                    if (this.volumeIndicator)
                        this.volumeIndicator.visible = false;
                });
                if (indicator) {
                    this.audioIndicator = indicator;
                }
                else {
                    console.error("Creating AudioIndicator failed");
                }
                // TODO: Relevant for Positional Audio
                /* this.positionalAudio = this.createAudioObject()
                this.applyRoomDepth()
                this.positionalAudio.matrixAutoUpdate = false
                this.positionalAudio.frustumCulled = false
                this.positionalAudio.autoplay = true
          
                if (!this.destination) {
                  this.destination = this.positionalAudio.context.createMediaStreamDestination()
                }
          
                if (this.microphoneSource) {
                  this.microphoneSource.disconnect()
                }
          
                this.microphoneSource = soundService.audioListener.context.createMediaStreamSource(this.mediaStream)
                const destinationSource = this.positionalAudio.context.createMediaStreamSource(this.destination.stream)
                this.microphoneSource.connect(this.destination)
                this.positionalAudio.setNodeSource(destinationSource as any)
          
                this.avatarGroup.add(this.positionalAudio)
                this.analyser = new AudioAnalyser(this.positionalAudio, 32)
                //this.applyRoomDepth()
                */
                if (clientId !== undefined) {
                    this.updatePlaybackVolume(clientId);
                }
                this.setIsMuted(false);
            }
        });
    }
    onPeerRoomChanged(_roomDepthDiff, audible) {
        this.isAudible = audible;
        const clientId = usersService.getClientIdByAvatarUid(this.uid);
        if (clientId !== undefined) {
            this.updatePlaybackVolume(clientId);
        }
        // TODO: Relevant for Positional Audio
        /*
        this.roomDepthDiffToPeer = roomDepthDiff
        //this.applyRoomDepth()
        this.updateMicStream()
        */
    }
    // TODO: Relevant for Positional Audio
    /* private applyRoomDepth() {
      if (!(this.positionalAudio instanceof PositionalAudio))
        return
  
      // When the Avatar is in another room, you should always hear him.
      let rolloffFactor = 0
  
      if (this.roomDepthDiffToPeer === 0) {
        rolloffFactor = preferencesService.getNumberPreferredValue("avatarAudioRolloffFactor", 1) as number
      }
  
      this.positionalAudio.setRolloffFactor(rolloffFactor)
    } */
    // TODO: Relevant for Positional Audio
    /* private createAudioObject() {
      const disablePositionalAudio = preferencesService.getBooleanPreferredValue("disablePositionalAudio", false)
  
      if (disablePositionalAudio) {
        return new ThreeAudio(soundService.audioListener)
      }
  
      const distanceModel = preferencesService.getPreferredValue("avatarAudioDistanceModel", "linear")
      const refDistance = preferencesService.getNumberPreferredValue("avatarAudioRefDistance", 10)
      const maxDistance = preferencesService.getNumberPreferredValue("avatarAudioMaxDistance", 100)
  
      const audio = new PositionalAudio(soundService.audioListener)
      if (distanceModel !== undefined) {
        audio.setDistanceModel(distanceModel)
      }
      if (refDistance !== undefined) {
        audio.setRefDistance(refDistance)
      }
      if (maxDistance !== undefined) {
        audio.setMaxDistance(maxDistance)
      }
      return audio
    } */
    setIsMuted(isMuted) {
        this.isMuted = isMuted;
        if (this.mutedIndicator) {
            this.mutedIndicator.visible = this.isMuted;
        }
    }
    setMutedIndicator(mutedIndicator) {
        this.mutedIndicator = mutedIndicator;
        if (this.mutedIndicator) {
            this.mutedIndicator.visible = this.isMuted;
        }
    }
    setVolumeIndicator(volumeIndicator) {
        this.volumeIndicator = volumeIndicator;
        this.volumeIndicator.visible = false;
    }
    setVolume(volume) {
        if (this.audioElement) {
            this.audioElement.volume = volume;
        }
        // TODO: Relevant for Positional Audio
        /* if (this.positionalAudio) {
          this.positionalAudio.setVolume(volume)
        } */
    }
    updatePlaybackVolume(clientId) {
        const isLocallyMuted = remoteStreamingService.isClientMuted(clientId);
        this.setVolume(this.isAudible && !isLocallyMuted ? 1 : 0);
    }
}
